import React, { useMemo, useState, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';

const StudentResearchForm = ({
    isOpen, onClose, formData, setFormData,
    isEditing, isReadOnly, toast, fetchData
}) => {
    const [pendingFiles, setPendingFiles] = useState([]);
    const [deletedFileIds, setDeletedFileIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPendingFiles([]);
            setDeletedFileIds([]);
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const existingFiles = useMemo(() => {
        if (!formData.AttachmentUrl) return [];
        try {
            const parsed = JSON.parse(formData.AttachmentUrl);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }, [formData.AttachmentUrl]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        if (isReadOnly) return;
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleDateChange = (name, dateObj) => {
        if (isReadOnly) return;
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString } });
    };

    const handleTitleBlur = async (e) => {
        if (isReadOnly) return;
        const titleText = e.target.value;
        if (!titleText || titleText.trim().length < 5) return;

        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            const userId = currentUser ? currentUser.Id : '';

            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = userRoleName === 'admin' || currentUser?.RoleId === 1 ||
                (userRoleName.includes('trưởng khoa') && currentUser?.IsDelegatedAdmin === true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/studentresearch?userId=${userId}&isAdmin=${isManager}`, {
                headers: {
                    'X-User-Role-Id': currentUser?.RoleId?.toString() || '',
                    'X-User-Dept-Id': currentUser?.DepartmentId?.toString() || ''
                }
            });

            if (response.ok) {
                const existingData = await response.json();

                const normalizeString = (str) => {
                    if (!str) return '';
                    return str.toLowerCase().trim()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                        .replace(/[^a-z0-9]/g, "");
                };

                const calculateSimilarity = (s, t) => {
                    if (!s || !t) return 0;
                    if (s === t) return 1;
                    const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                    for (let i = 0; i <= s.length; i++) d[i][0] = i;
                    for (let j = 0; j <= t.length; j++) d[0][j] = j;
                    for (let j = 1; j <= t.length; j++) {
                        for (let i = 1; i <= s.length; i++) {
                            const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                        }
                    }
                    return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
                };

                const normInput = normalizeString(titleText);
                const currentId = isEditing ? formData.Id : 0;
                const inputYear = formData.StartDate ? formData.StartDate.substring(0, 4) : '';

                for (let item of existingData) {
                    if (item.Id === currentId) continue;

                    const normExisting = normalizeString(item.TopicName);
                    const sim = calculateSimilarity(normInput, normExisting);
                    const existingYear = item.StartDate ? item.StartDate.substring(0, 4) : '';

                    if (sim > 0.90) {
                        if (sim > 0.98 || existingYear === inputYear) {
                            toast?.current?.show({
                                severity: 'warn',
                                summary: 'Cảnh báo trùng lặp',
                                detail: `Hệ thống phát hiện Đề tài HD Sinh viên rất giống đã tồn tại: "${item.TopicName}". Vui lòng kiểm tra lại!`,
                                life: 8000
                            });
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Lỗi check trùng lặp:", err);
        }
    };

    const handleMultiFileChange = (e) => {
        if (isReadOnly) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const validFiles = files.filter(file => {
            const isTypeValid = allowedTypes.includes(file.type);
            const fileName = file.name.toLowerCase();
            const isExtensionValid = allowedExtensions.some(ext => fileName.endsWith(ext));

            if (!isTypeValid && !isExtensionValid) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Định dạng không hợp lệ',
                    detail: `Tệp "${file.name}" không được hỗ trợ. Chỉ cho phép PDF, JPG/JPEG, PNG`,
                    life: 5000
                });
                return false;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Dung lượng quá lớn',
                    detail: `Tệp "${file.name}" vượt quá giới hạn 5MB. Vui lòng chọn tệp nhỏ hơn.`,
                    life: 5000
                });
                return false;
            }

            return true;
        });

        if (validFiles.length === 0) {
            e.target.value = null;
            return;
        }

        const newFilesArray = validFiles.map(file => ({
            fileObj: file,
            name: file.name,
            description: ''
        }));

        setPendingFiles(prev => [...prev, ...newFilesArray]);
        e.target.value = null;
    };

    const handleFileDescChange = (index, desc) => {
        if (isReadOnly) return;
        const updated = [...existingFiles];
        updated[index].description = desc;
        setFormData({ ...formData, AttachmentUrl: JSON.stringify(updated) });
    };

    const removeExistingFile = (fileId) => {
        if (isReadOnly) return;
        setDeletedFileIds(prev => [...prev, fileId]);
        const updated = existingFiles.filter(f => f.fileId !== fileId && f.url !== fileId && f.link !== fileId && f.data !== fileId);
        setFormData({ ...formData, AttachmentUrl: JSON.stringify(updated) });
    };

    const removePendingFile = (index) => {
        if (isReadOnly) return;
        setPendingFiles(pendingFiles.filter((_, i) => i !== index));
    };

    const downloadIndividualFile = async (fileObj) => {
        let fileUrl = '';
        if (fileObj.fileId) {
            fileUrl = `${process.env.REACT_APP_API_BASE_URL}/downloadstudentresearchevidence?fileId=${fileObj.fileId}`;
        } else if (fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL) {
            fileUrl = fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL;
            if (typeof fileUrl === 'string') {
                fileUrl = fileUrl.trim();
                if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                    fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
                }
            }
        }

        if (!fileUrl) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy đường dẫn tệp!', life: 3000 });
            return;
        }

        const base64ToBlob = (dataUrl) => {
            const arr = dataUrl.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new Blob([u8arr], { type: mime });
        };

        try {
            if (typeof fileUrl === 'string' && fileUrl.startsWith('data:')) {
                const blob = base64ToBlob(fileUrl);
                const objectUrl = URL.createObjectURL(blob);

                const newWindow = window.open(objectUrl, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                return;
            }

            const newWindow = window.open(fileUrl, '_blank');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Lỗi khi xử lý mở file:", error);
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể hiển thị tệp này do lỗi định dạng!', life: 3000 });
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (isReadOnly) return;

        if (existingFiles.length === 0 && pendingFiles.length === 0) {
            toast?.current?.show({
                severity: 'warn',
                summary: 'Thiếu thông tin',
                detail: 'Vui lòng đính kèm ít nhất một tệp tin minh chứng!',
                life: 4000
            });
            return;
        }

        const userStr = localStorage.getItem('user');
        if (!userStr) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Hết phiên đăng nhập!' });
            return;
        }

        setIsSubmitting(true);
        const currentUser = JSON.parse(userStr);
        const method = isEditing ? 'PUT' : 'POST';

        let cleanEvidence = [];
        try {
            const currentFiles = JSON.parse(formData.AttachmentUrl || '[]');
            cleanEvidence = currentFiles.filter(f => !f.fileId);
        } catch (e) { }

        let targetUserId = currentUser.Id;
        if (formData.UserId && formData.UserId.toString() !== currentUser.Id.toString()) {
            targetUserId = formData.UserId;
        }

        const payload = {
            ...formData,
            UserId: parseInt(targetUserId),
            StudentCount: parseInt(formData.StudentCount) || 0,
            AttachmentUrl: JSON.stringify(cleanEvidence)
        };

        if (isEditing) payload.Id = formData.Id;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/studentresearch`, {
                method,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Phiên đăng nhập hết hạn!' });
                return;
            }

            let result;
            try {
                result = await response.json();
            } catch (err) {
                result = { status: 'error', message: 'Lỗi máy chủ không xác định!' };
            }

            if (response.ok && result.status !== 'error') {
                const finalId = result.studentResearchId || formData.Id;

                if (deletedFileIds.length > 0) {
                    await Promise.all(deletedFileIds.map(async (fId) => {
                        const parsedId = parseInt(fId, 10);
                        if (!isNaN(parsedId)) {
                            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/deletestudentresearchevidence?fileId=${parsedId}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
                            });

                            if (!res.ok) {
                                console.error(`Không thể xóa file cũ có ID: ${parsedId}`);
                            }
                        }
                    }));
                }

                if (pendingFiles.length > 0) {
                    await Promise.all(pendingFiles.map(async (pf) => {
                        const fd = new FormData();
                        fd.append('studentResearchId', finalId);
                        fd.append('description', pf.description);
                        fd.append('file', pf.fileObj);

                        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/uploadstudentresearchevidence`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
                            body: fd
                        });

                        if (!res.ok) {
                            const err = await res.json().catch(() => ({}));
                            throw new Error(err.message || `Upload file ${pf.name} thất bại!`);
                        }
                    }));
                }

                toast.current.show({ severity: 'success', summary: 'Thành công', detail: isEditing ? 'Cập nhật thành công' : 'Thêm mới thành công' });
                if (typeof fetchData === 'function') fetchData(true);
                onClose();
            } else {
                const errorMsg = result.message || 'Lưu dữ liệu thất bại!';
                toast.current.show({ severity: 'error', summary: 'Không thể lưu', detail: errorMsg, life: 6000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: error.message || 'Lỗi kết nối máy chủ' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box form-modal-box" style={{ maxHeight: '95vh', overflowY: 'auto' }}>

                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>
                        {isReadOnly ? "Chi tiết đăng ký sinh viên NCKH" : (isEditing ? "Cập nhật đăng ký" : "Thông tin đăng ký sinh viên nghiên cứu khoa học")}
                    </h3>
                    <button className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>
                        &times;
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="srForm" onSubmit={handleFormSubmit}>
                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tên đề tài *</label>
                            <input
                                type="text"
                                name="TopicName"
                                className="form-input"
                                placeholder="Nhập tên đề tài"
                                value={formData.TopicName || ''}
                                onChange={handleChange}
                                onBlur={handleTitleBlur}
                                required
                                disabled={isReadOnly}
                            />
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '15px' }}>
                            <div className="form-group" style={{ flex: '2 1 300px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Danh sách sinh viên *</label>
                                <input
                                    type="text"
                                    name="StudentList"
                                    className="form-input"
                                    value={formData.StudentList || ''}
                                    onChange={handleChange}
                                    required
                                    disabled={isReadOnly}
                                />
                            </div>
                            <div className="form-group" style={{ flex: '1 1 150px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số lượng *</label>
                                <input
                                    type="number"
                                    name="StudentCount"
                                    className="form-input"
                                    placeholder="Nhập số lượng"
                                    value={formData.StudentCount || ''}
                                    onChange={handleChange}
                                    required
                                    disabled={isReadOnly}
                                    min="1"
                                />
                            </div>
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày</label>
                                <Calendar
                                    id="StartDate"
                                    name="StartDate"
                                    value={(formData.StartDate && !isNaN(new Date(formData.StartDate).getTime())) ? new Date(formData.StartDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('StartDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isReadOnly}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày</label>
                                <Calendar
                                    id="EndDate"
                                    name="EndDate"
                                    value={(formData.EndDate && !isNaN(new Date(formData.EndDate).getTime())) ? new Date(formData.EndDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('EndDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tệp tin minh chứng *</label>

                            <p style={{ color: '#e74c3c', fontStyle: 'italic', marginBottom: '15px', marginTop: '-5px', fontSize: '14px' }}>
                                * Chú ý: Hệ thống chỉ nhận tệp minh chứng ở định dạng PDF, JPG/JPEG và PNG. Dung lượng tối đa 5MB / tệp
                            </p>

                            {(existingFiles.length > 0 || pendingFiles.length > 0) && (
                                <div style={{ overflowX: 'auto', marginBottom: '15px' }}>
                                    <table className="custom-table" style={{ width: '100%', minWidth: '500px', border: '1px solid #eee', marginBottom: 0 }}>
                                        <thead style={{ backgroundColor: '#f9f9f9' }}>
                                            <tr>
                                                <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                <th>Mô tả</th>
                                                <th width="35%">Minh chứng</th>
                                                {!isReadOnly && <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {existingFiles.map((file, idx) => (
                                                <tr key={`ext-${idx}`}>
                                                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                    <td>
                                                        {isReadOnly ? (
                                                            <span>{file.description || ''}</span>
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                className="form-input"
                                                                placeholder="Nhập thông tin mô tả"
                                                                value={file.description || ''}
                                                                onChange={(e) => handleFileDescChange(idx, e.target.value)}
                                                                style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span onClick={() => downloadIndividualFile(file)} style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }} title="Nhấn để tải về">
                                                            {file.name}
                                                        </span>
                                                    </td>
                                                    {!isReadOnly && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button type="button" onClick={() => removeExistingFile(file.fileId || file.url || file.data || file.link)} style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                            {pendingFiles.map((file, idx) => (
                                                <tr key={`pend-${idx}`}>
                                                    <td style={{ textAlign: 'center' }}>{existingFiles.length + idx + 1}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            placeholder="Nhập thông tin mô tả"
                                                            value={file.description || ''}
                                                            onChange={(e) => {
                                                                const newFiles = [...pendingFiles];
                                                                newFiles[idx].description = e.target.value;
                                                                setPendingFiles(newFiles);
                                                            }}
                                                            style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                            disabled={isReadOnly}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span style={{ color: '#2ecc71', wordBreak: 'break-all' }}>
                                                            {file.name} <i className="fa-solid fa-cloud-arrow-up"></i> (Mới)
                                                        </span>
                                                    </td>
                                                    {!isReadOnly && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button type="button" onClick={() => removePendingFile(idx)} style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!isReadOnly && (
                                <div style={{ border: '2px dashed #3498db', borderRadius: '8px', padding: '30px', textAlign: 'center', backgroundColor: '#ebf5fb', position: 'relative', overflow: 'hidden' }}>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png"
                                        onChange={handleMultiFileChange}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                        title="Nhấn để chọn tệp"
                                    />
                                    {(existingFiles.length > 0 || pendingFiles.length > 0) ? (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '35px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>Đã chọn {existingFiles.length + pendingFiles.length} tệp minh chứng</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Thêm tệp tin khác</button>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '40px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>Chưa có tệp nào được chọn</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Chọn tệp từ thiết bị</button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                    </form>
                </div>

                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px' }}>
                    <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
                        {isReadOnly ? "Đóng" : "Hủy"}
                    </button>
                    {!isReadOnly && (
                        <button type="submit" form="srForm" className="btn-submit" disabled={isSubmitting}>
                            {isSubmitting ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '5px' }}></i> Đang xử lý...</> : "Hoàn tất"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentResearchForm;